import React, { useEffect, useState } from 'react';
import { Table, message } from 'antd';
import { getReportApiByKey } from '@app/api/reports.api';

interface CMSummaryRow {
  key: string | number;

  cashier_code: string;
  cashier_location: string;

  department?: string;
  department_total?: number;

  receipt_count?: number;
  total_earnings?: number;

  children?: CMSummaryRow[];
}

interface CMSummaryReportProps {
  reportKey: string;
  filters: any;
}

const CMSummaryReport: React.FC<CMSummaryReportProps> = ({ reportKey, filters }) => {
  const [data, setData] = useState<CMSummaryRow[]>([]);
  const [loading, setLoading] = useState(false);

  const withDetail = Boolean(filters?.with_detail);

  useEffect(() => {
    const fetchData = async () => {
      if (!reportKey || !filters) return;

      const api = getReportApiByKey(reportKey);
      if (!api) {
        message.error('Invalid report key.');
        return;
      }

      setLoading(true);
      try {
        const response = await api.generateReport(filters);
        const reportData = response?.report || {};

        const rows: CMSummaryRow[] = Object.values(reportData).map((item: any, index: number) => {
          const parentRow: CMSummaryRow = {
            key: index,
            cashier_code: item.code,
            cashier_location: item.name,
            receipt_count: item.receipt_count,
            total_earnings: item.receipt_total,
          };

          // Cascade only when with_detail = true
          if (withDetail && Array.isArray(item.departments)) {
            parentRow.children = item.departments.map((dept: any, deptIndex: number) => ({
              key: `${index}-${deptIndex}`,
              department: dept.name,
              department_total: dept.total,
            }));
          }

          return parentRow;
        });

        setData(rows);
      } catch (err) {
        console.error(err);
        message.error('Failed to fetch CM Summary Report data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [reportKey, filters, withDetail]);

  /* =========================
     Columns
  ========================== */

  const columns: any[] = [
    {
      title: 'Cashier Code',
      dataIndex: 'cashier_code',
      key: 'cashier_code',
    },
    {
      title: 'Cashier Location',
      dataIndex: 'cashier_location',
      key: 'cashier_location',
    },
  ];

  // Insert department columns BEFORE totals
  if (withDetail) {
    columns.push(
      {
        title: 'Department',
        dataIndex: 'department',
        key: 'department',
        render: (v: string) => v || '-',
      },
      {
        title: 'Department Total (Rs.)',
        dataIndex: 'department_total',
        key: 'department_total',
        align: 'right',
        render: (v: number) => (v !== undefined ? v.toLocaleString('en-LK', { minimumFractionDigits: 2 }) : '-'),
      },
    );
  }

  // Always keep totals last
  columns.push(
    {
      title: 'No of Receipts',
      dataIndex: 'receipt_count',
      key: 'receipt_count',
      align: 'right',
      render: (v: number) => (v !== undefined ? v : '-'),
    },
    {
      title: 'Total Earnings (Rs.)',
      dataIndex: 'total_earnings',
      key: 'total_earnings',
      align: 'right',
      render: (v: number) => (v !== undefined ? v.toLocaleString('en-LK', { minimumFractionDigits: 2 }) : '-'),
    },
  );

  return (
    <Table
      columns={columns}
      dataSource={data}
      loading={loading}
      bordered
      pagination={false}
      expandable={withDetail ? { defaultExpandAllRows: true } : undefined}
    />
  );
};

export default CMSummaryReport;
